import pRetry from 'p-retry';
import * as io from 'socket.io-client';
import * as config from '../config';
import * as utils from '../../utils';
import { getLogger } from '../logger';

const MAX_SOCKET_RECONNECT_ATTEMPT = 50;
const MAX_RECONNECT_ATTEMPT_BEFORE_SWITCH = 10;
const EMIT_PROMISE_TIMEOUT = 5000;
const POLLING_TRANSPORT_TIMEOUT_MS = 10 * 1000;

const logger = getLogger('base socket service');

export class BaseSocketService {
    attempts = 0;
    rooms: { [testResultId: string]: string | undefined } = {};
    emitPromiseQueue: undefined | Promise<void> = undefined;
    emitJoinRoom?(resultId: string, testId?: string): void;
    url?: string;
    _socket?: io.Socket;
    isAllowedWS?: boolean;
    prevErr?: any;
    onConnect?(): void;

    joinToMultipleResults() {
        const testResultIds = Object.keys(this.rooms);
        logger.info('re-join all existing rooms', { testResultIds });
        testResultIds.forEach(resultId => {
            const testId = this.rooms[resultId];
            this.emitJoinRoom?.(resultId, testId);
        });
    }

    joinRoom(resultId: string, testId?: string) {
        this.rooms[resultId] = testId;
    }

    leaveRoom(resultId: string) {
        delete this.rooms[resultId];
    }

    addSocketHandlers() {
        const socketError = (method: string, err: any) => {
            let transport = 'websocket';
            try {
                transport = this._socket!.io.engine.transport.name;
            } catch (e) {
                /* empty */
            }
            logger.error(`Error in SocketService websocket _${method}_ socket ${this._socket!.id} ` +
            `is ${this.url} over ${transport}. Reconnect attempts ${this.attempts}. Error is: ${err?.message}`);
        };

        this._socket!.on('reconnect_attempt', (attempt) => {
            socketError('reconnect_attempt', { message: 'reconnect attempt', attempt });
            this.attempts++;
            if (this.attempts === MAX_RECONNECT_ATTEMPT_BEFORE_SWITCH && !this.isAllowedWS) {
                this._socket!.io.opts.transports = ['polling'];
                this._socket!.io.opts.upgrade = false;
            }
            if (this.attempts >= MAX_SOCKET_RECONNECT_ATTEMPT) {
                throw new Error('Can\'t connect to Testim Servers.\n' +
                    `Action required: Please allow opening a websockets connection to ${config.SERVICES_HOST()} in your firewall/proxy`);
            }
        });

        this._socket!.on('connect_error', (err) => {
            socketError('connect_error', err);
        });

        this._socket!.on('connect_timeout', (err) => {
            socketError('connect_timeout', err);
        });

        this._socket!.on('error', err => {
            socketError('error', err);
        });

        this._socket!.on('reconnect_error', err => {
            if (this.prevErr && this.prevErr.type === err.type) {
                return;
            }
            this.prevErr = err;
            socketError('reconnect_error', err);
        });

        this._socket!.on('reconnect', () => {
            logger.info('reconnect to socket and re-join to rooms');
            this.joinToMultipleResults();
        });

        this._socket!.on('connect', () => {
            this.attempts = 0;
            if (this.isAllowedWS === undefined) {
                this.isAllowedWS = this._socket!.io.engine.transport && this._socket!.io.engine.transport.name === 'websocket';
            }
            this.onConnect?.();
        });
    }

    initNewSocket(projectId: string, ns: string) {
        const opts = {
            query: { projectId },
            requestTimeout: POLLING_TRANSPORT_TIMEOUT_MS,
            transports: ['websocket'],
            upgrade: false,
            forceNew: true,
            rejectUnauthorized: process.env.NODE_TLS_REJECT_UNAUTHORIZED !== '0',
            ...(global.caFileContent && { ca: global.caFileContent as string }),
            ...(global.proxyUri && { agent: new global.ProxyAgent!(global.proxyUri) as any }),
        };

        return new Promise<void>(resolve => {
            this.url = `${config.SERVICES_HOST()}/${ns}`;
            this._socket = io.connect(this.url, opts);
            this.addSocketHandlers();
            this._socket.on('connect', resolve);
            this._socket.open();
        });
    }

    init(projectId: string, ns: string) {
        const opts = {
            query: { projectId },
            requestTimeout: POLLING_TRANSPORT_TIMEOUT_MS,
            transports: ['websocket'],
            upgrade: false,
            rejectUnauthorized: process.env.NODE_TLS_REJECT_UNAUTHORIZED !== '0',
            ...(global.caFileContent && { ca: global.caFileContent as string }),
            ...(global.proxyUri && { agent: new global.ProxyAgent!(global.proxyUri) as any }),
        };

        this.url = `${config.SERVICES_HOST()}/${ns}`;
        this._socket = io.connect(this.url, opts);
        this.addSocketHandlers();
    }

    emitPromise(eventName: string, eventData: any) {
        const errorneousEvents: { [eventName: string]: any } = {};

        const emitAndWait = () => new Promise<void>((resolve, reject) => {
            this._socket!.emit(eventName, eventData, (data?: { success?: boolean }) => {
                if (data?.success) {
                    return resolve();
                }
                errorneousEvents[eventName] = eventData;

                return reject(new Error('bad ack'));
            });
        });

        this.emitPromiseQueue = (this.emitPromiseQueue || Promise.resolve())
            .then(() => pRetry(() => utils.promiseTimeout(emitAndWait(), EMIT_PROMISE_TIMEOUT), { retries: 200, minTimeout: 3000, factor: 1 }))
            .finally(() => {
                if (Object.keys(errorneousEvents).length > 0) {
                    logger.error('Bad acknowledge from socket emit', { errorneousEvents });
                }
            });

        return this.emitPromiseQueue;
    }
}
